/**
 * useHomebrewWizard.js — useReducer-based state management for composed mechanic JSON.
 *
 * Provides the shared config state and dispatch that all composer sections
 * read from and write to. The shape matches the composed mechanic schema
 * defined in src/engine/schema.js.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import { useReducer } from 'react';

const createInitialState = (initialMechanic) => {
  if (initialMechanic) return initialMechanic;
  return {
    id: '',
    version: '1.0',
    meta: {
      name: '',
      description: '',
      source: 'homebrew',
      containerContext: 'ability',
      containerType: 'class',
    },
    actions: [],
    tokens: [],
    passives: [],
    display: {},
  };
};

let actionIdCounter = 0;
const nextActionId = () => `action_${Date.now()}_${++actionIdCounter}`;

let tokenIdCounter = 0;
const nextTokenId = () => `token_${Date.now()}_${++tokenIdCounter}`;

function wizardReducer(state, action) {
  switch (action.type) {
    // ── Top-level ID ──
    case 'SET_ID':
      return { ...state, id: action.value };

    // ── Meta ──
    case 'SET_META_FIELD':
      return {
        ...state,
        meta: { ...state.meta, [action.field]: action.value },
      };

    // ── Actions ──
    case 'ADD_ACTION':
      return {
        ...state,
        actions: [
          ...state.actions,
          {
            id: nextActionId(),
            label: '',
            variant: 'primary',
            effects: [],
            conditions: [],
            costs: [],
          },
        ],
      };

    case 'UPDATE_ACTION':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex ? { ...a, [action.field]: action.value } : a
        ),
      };

    case 'DELETE_ACTION':
      return {
        ...state,
        actions: state.actions.filter((_, i) => i !== action.actionIndex),
      };

    // ── Conditions ──
    case 'ADD_CONDITION':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, conditions: [...(a.conditions || []), action.condition || { type: 'resource_available', resource: 'hope' }] }
            : a
        ),
      };

    case 'UPDATE_CONDITION':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? {
                ...a,
                conditions: (a.conditions || []).map((c, j) =>
                  j === action.conditionIndex ? { ...c, ...action.updates } : c
                ),
              }
            : a
        ),
      };

    case 'REMOVE_CONDITION':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, conditions: (a.conditions || []).filter((_, j) => j !== action.conditionIndex) }
            : a
        ),
      };

    // ── Costs ──
    case 'ADD_COST':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, costs: [...(a.costs || []), action.cost || { type: 'mark_stress', amount: 1 }] }
            : a
        ),
      };

    case 'UPDATE_COST':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? {
                ...a,
                costs: (a.costs || []).map((c, j) =>
                  j === action.costIndex ? { ...c, ...action.updates } : c
                ),
              }
            : a
        ),
      };

    case 'REMOVE_COST':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, costs: (a.costs || []).filter((_, j) => j !== action.costIndex) }
            : a
        ),
      };

    // ── Effects ──
    case 'ADD_EFFECT':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, effects: [...(a.effects || []), action.effect || { type: 'gain_hope', amount: 1 }] }
            : a
        ),
      };

    case 'UPDATE_EFFECT':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? {
                ...a,
                effects: (a.effects || []).map((e, j) =>
                  j === action.effectIndex ? { ...e, ...action.updates } : e
                ),
              }
            : a
        ),
      };

    case 'REMOVE_EFFECT':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex
            ? { ...a, effects: (a.effects || []).filter((_, j) => j !== action.effectIndex) }
            : a
        ),
      };

    case 'REORDER_EFFECTS': {
      const { actionIndex, fromIndex, toIndex } = action;
      return {
        ...state,
        actions: state.actions.map((a, i) => {
          if (i !== actionIndex) return a;
          const effects = [...(a.effects || [])];
          const [moved] = effects.splice(fromIndex, 1);
          effects.splice(toIndex, 0, moved);
          return { ...a, effects };
        }),
      };
    }

    // ── Tokens ──
    case 'ADD_TOKEN':
      return {
        ...state,
        tokens: [
          ...state.tokens,
          { id: nextTokenId(), name: '', max: 1, refreshOn: 'session' },
        ],
      };

    case 'UPDATE_TOKEN':
      return {
        ...state,
        tokens: state.tokens.map((t, i) =>
          i === action.tokenIndex ? { ...t, ...action.updates } : t
        ),
      };

    case 'REMOVE_TOKEN':
      return {
        ...state,
        tokens: state.tokens.filter((_, i) => i !== action.tokenIndex),
      };

    // ── Passives ──
    case 'ADD_PASSIVE':
      return {
        ...state,
        passives: [
          ...state.passives,
          { stat: 'evasion', amount: 1 },
        ],
      };

    case 'UPDATE_PASSIVE':
      return {
        ...state,
        passives: state.passives.map((p, i) =>
          i === action.passiveIndex ? { ...p, ...action.updates } : p
        ),
      };

    case 'REMOVE_PASSIVE':
      return {
        ...state,
        passives: state.passives.filter((_, i) => i !== action.passiveIndex),
      };

    // ── Display ──
    case 'SET_DISPLAY':
      return {
        ...state,
        display: { ...state.display, ...action.updates },
      };

    // ── Toggle Off ──
    case 'SET_TOGGLE_OFF':
      return {
        ...state,
        actions: state.actions.map((a, i) =>
          i === action.actionIndex ? { ...a, toggleOff: action.toggleOff } : a
        ),
      };

    // ── Load existing mechanic ──
    case 'LOAD_MECHANIC':
      return action.mechanic;

    default:
      return state;
  }
}

/**
 * Hook for managing composed mechanic state in the Mechanic Composer wizard.
 *
 * @param {object} [initialMechanic] - Optional existing mechanic to edit
 * @returns {{ config: object, dispatch: function }}
 */
export function useHomebrewWizard(initialMechanic) {
  const [config, dispatch] = useReducer(wizardReducer, initialMechanic, createInitialState);
  return { config, dispatch };
}
